import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../api/http.js';
import { useAdminAuth } from '../auth/AdminAuthContext.jsx';
import { useUI } from '../ui/UIProvider.jsx';
import { Icon } from '../components/icons.jsx';

/**
 * Admin Team page. Lists every admin console account, lets a
 * Superadmin invite new operators, change roles, disable / re-enable,
 * and delete. Wired to AdminAdminsController:
 *
 *   GET    /api/admin/admins              — list (any role)
 *   POST   /api/admin/admins/invite       — invite (SUPERADMIN)
 *   PUT    /api/admin/admins/{id}/role    — change role (SUPERADMIN)
 *   PUT    /api/admin/admins/{id}/disable — toggle disabled (SUPERADMIN)
 *   DELETE /api/admin/admins/{id}         — delete (SUPERADMIN)
 *
 * Invite flow: the server returns a one-shot tempPassword in the
 * response body. We surface it in a dedicated success panel with a
 * copy button — the password is never recoverable after the modal
 * closes (server stores only the bcrypt hash). The page text
 * explicitly tells the Superadmin to share it out-of-band; the
 * server-side comment makes the same point.
 *
 * Non-Superadmins can VIEW the table (so support agents can see who
 * else is around) but every mutation button is hidden — the matching
 * @PreAuthorize on the server would 403 them anyway, but a friendly
 * client-side guard beats a toast.
 *
 * @author <lukman@zshift.io>
 */

// Role catalog — mirror of the server's AdminRole enum. Centralised
// here so the invite form, the role pill colors, and the change-role
// dropdown all agree on the same labels.
//
// History: an earlier READ_ONLY analyst role lived here. It was
// removed because "view metrics only" is already covered by
// SUPPORT_AGENT minus mutations; the AdminRoleMigrator server-side
// rewrites any leftover rows.
const ROLE_OPTIONS = [
  {
    value: 'SUPERADMIN',
    label: 'Superadmin',
    blurb: 'Full access. Can manage admins, edit plans, refund any amount.',
    color: 'purple',
  },
  {
    value: 'SUPPORT_LEAD',
    label: 'Support lead',
    blurb: 'Views users + queries, replies, bans, refunds up to £200. Can invite Support Agents.',
    color: 'blue',
  },
  {
    value: 'SUPPORT_AGENT',
    label: 'Support agent (Customer Support)',
    blurb: 'Views users + queries, replies, refunds up to £50. The default for new hires.',
    color: 'green',
  },
];

// Which target roles is the current admin allowed to invite? Mirrors
// AdminRole.canInvite() on the server so the dropdown only shows
// what would actually succeed:
//   SUPERADMIN    → any role
//   SUPPORT_LEAD  → SUPPORT_AGENT only
//   SUPPORT_AGENT → nothing (doesn't see the Invite button)
function invitableRoles(myRole) {
  if (myRole === 'SUPERADMIN')   return ROLE_OPTIONS;
  if (myRole === 'SUPPORT_LEAD') return ROLE_OPTIONS.filter((r) => r.value === 'SUPPORT_AGENT');
  return [];
}

const ROLE_LABELS = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label]));
const ROLE_COLORS = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.color]));

export function Admins() {
  const { me } = useAdminAuth();
  const { toast, confirm } = useUI();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite modal: when open, shows the form OR (after a successful
  // POST) the credentials handoff panel. `inviteResult` holds the
  // server's response so the temp password survives the form unmount
  // and stays visible until the operator dismisses the modal.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // Change-role popover state. `null` when closed; otherwise the row.
  const [roleEditing, setRoleEditing] = useState(null);

  // Two separate permission gates, both mirrored from the server:
  //
  //   canInvite     — show the "Invite admin" button. Superadmins can
  //                   invite anyone; Support Leads can invite Support
  //                   Agents only. Support Agents never see the button.
  //
  //   canManageRoles — show the per-row Role / Disable / Delete buttons.
  //                    These mutate the access-control surface itself
  //                    (changeRole, disable, delete are all SUPERADMIN-
  //                    only on the server), so we restrict them here
  //                    even though a Support Lead can invite.
  //
  // The split matters: a Support Lead can ADD a new Support Agent but
  // can't demote an existing Superadmin to one. That asymmetry is
  // exactly what AdminRole.canInvite() encodes server-side.
  const canInvite      = invitableRoles(me?.role).length > 0;
  const canManageRoles = me?.role === 'SUPERADMIN';

  // ── Fetch ──────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/api/admin/admins');
      setAdmins(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.message || 'Could not load admin team.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Mutations ──────────────────────────────────────────────────
  const handleInvite = useCallback(async (payload) => {
    const res = await api('/api/admin/admins/invite', {
      method: 'POST',
      body: payload,
    });
    setInviteResult(res);
    refresh();
    return res;
  }, [refresh]);

  const handleChangeRole = useCallback(async (id, role) => {
    try {
      await api(`/api/admin/admins/${id}/role`, {
        method: 'PUT',
        body: { role },
      });
      toast.success(`Role updated to ${ROLE_LABELS[role] || role}.`);
      setRoleEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.message || 'Could not change role.');
    }
  }, [toast, refresh]);

  const handleToggleDisabled = useCallback(async (admin) => {
    const willDisable = !admin.disabled;
    if (willDisable) {
      const ok = await confirm({
        title: `Disable ${admin.name || admin.email}?`,
        message: 'They will be unable to sign in until you re-enable them. Active sessions stay until the token expires.',
        confirmLabel: 'Disable',
        destructive: true,
      });
      if (!ok) return;
    }
    try {
      await api(`/api/admin/admins/${admin.id}/disable`, {
        method: 'PUT',
        body: { disabled: willDisable },
      });
      toast.success(willDisable ? 'Admin disabled.' : 'Admin re-enabled.');
      refresh();
    } catch (e) {
      toast.error(e?.message || 'Could not update status.');
    }
  }, [toast, confirm, refresh]);

  const handleDelete = useCallback(async (admin) => {
    const ok = await confirm({
      title: `Delete ${admin.name || admin.email}?`,
      message: 'This permanently removes the account. They\'ll be locked out immediately. Audit log entries they generated are preserved.',
      confirmLabel: 'Delete admin',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api(`/api/admin/admins/${admin.id}`, { method: 'DELETE' });
      toast.success('Admin deleted.');
      refresh();
    } catch (e) {
      toast.error(e?.message || 'Could not delete admin.');
    }
  }, [toast, confirm, refresh]);

  // ── Derived stats ──────────────────────────────────────────────
  const counts = useMemo(() => {
    const c = { total: admins.length, byRole: {}, disabled: 0 };
    for (const a of admins) {
      c.byRole[a.role] = (c.byRole[a.role] || 0) + 1;
      if (a.disabled) c.disabled++;
    }
    return c;
  }, [admins]);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Admin <em>team</em></h1>
          <p className="page-sub">
            Who can sign in to this console, what they can do, and when they last did it.
            Only Superadmins can invite, change roles, or remove accounts.
          </p>
        </div>
        {canInvite && (
          <button
            className="btn primary"
            onClick={() => { setInviteResult(null); setInviteOpen(true); }}
          >
            <Icon name="mail" size={13} />
            <span style={{ marginLeft: 6 }}>Invite admin</span>
          </button>
        )}
      </div>

      {/* ── Quick-stat strip ────────────────────────────────────── */}
      <div className="row r-1-1" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <StatCard label="Total admins"   value={counts.total} loading={loading} />
        <StatCard label="Superadmins"    value={counts.byRole.SUPERADMIN || 0} loading={loading} />
        <StatCard label="Support staff"
                  value={(counts.byRole.SUPPORT_LEAD || 0) + (counts.byRole.SUPPORT_AGENT || 0)}
                  loading={loading} />
        <StatCard label="Disabled"       value={counts.disabled} loading={loading}
                  tone={counts.disabled > 0 ? 'warn' : null} />
      </div>

      {/* ── Table ───────────────────────────────────────────────── */}
      <div className="row">
        <div className="card">
          <div className="card-head">
            <h3>Team members</h3>
            <div className="sub">Sorted by last-login desc</div>
          </div>
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last login</th>
                  <th>Joined</th>
                  {canManageRoles && <th style={{ width: 140, textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={canManageRoles ? 6 : 5} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
                    Loading…
                  </td></tr>
                )}
                {!loading && admins.length === 0 && (
                  <tr><td colSpan={canManageRoles ? 6 : 5} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
                    No admins yet.
                  </td></tr>
                )}
                {!loading && admins.map((a) => {
                  const isMe = me?.id === a.id;
                  return (
                    <tr key={a.id} style={{ opacity: a.disabled ? 0.55 : 1 }}>
                      <td>
                        <div className="user-cell">
                          <div className={`av v${(a.id % 10) + 1}`}>{initials(a.name || a.email)}</div>
                          <div>
                            <strong>
                              {a.name || '—'}
                              {isMe && <span style={{ color: 'var(--ink-3)', fontWeight: 400, marginLeft: 6 }}>(you)</span>}
                            </strong>
                            <em>{a.email}</em>
                          </div>
                        </div>
                      </td>
                      <td>
                        {roleEditing?.id === a.id ? (
                          <RolePicker
                            current={a.role}
                            onPick={(next) => handleChangeRole(a.id, next)}
                            onCancel={() => setRoleEditing(null)}
                          />
                        ) : (
                          <span
                            className={`pill role-${ROLE_COLORS[a.role] || 'grey'}`}
                            title={a.roleLabel || a.role}
                          >
                            <span className="dot"/> {a.roleLabel || ROLE_LABELS[a.role] || a.role}
                          </span>
                        )}
                      </td>
                      <td>
                        {a.disabled
                          ? <span className="pill warn"><span className="dot"/> disabled</span>
                          : <span className="pill success"><span className="dot"/> active</span>}
                      </td>
                      <td className="mono" style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                        {a.lastLoginAt ? agoString(a.lastLoginAt) : 'never'}
                        {a.lastLoginIp && (
                          <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{a.lastLoginIp}</div>
                        )}
                      </td>
                      <td className="mono" style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                        {a.createdAt ? agoString(a.createdAt) : '—'}
                      </td>
                      {canManageRoles && (
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: 4 }}>
                            {roleEditing?.id !== a.id && (
                              <button
                                className="btn ghost sm"
                                onClick={() => setRoleEditing(a)}
                                title="Change role"
                              >
                                Role
                              </button>
                            )}
                            <button
                              className="btn ghost sm"
                              onClick={() => handleToggleDisabled(a)}
                              title={a.disabled ? 'Re-enable' : 'Disable'}
                            >
                              {a.disabled ? 'Enable' : 'Disable'}
                            </button>
                            <button
                              className="btn ghost sm"
                              onClick={() => handleDelete(a)}
                              title="Delete admin"
                              disabled={isMe}
                              style={isMe ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                            >
                              <Icon name="x" size={12} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!canManageRoles && (
            <div style={{
              padding: '10px 16px',
              borderTop: '1px solid var(--line)',
              fontSize: 12,
              color: 'var(--ink-3)',
            }}>
              <strong style={{ color: 'var(--ink-2)' }}>Limited view · </strong>
              {canInvite
                ? 'You can invite new Support Agents, but role changes, disable, and delete are Superadmin-only. Ask a Superadmin for those.'
                : 'Only Superadmins can invite, change roles, or remove accounts. Ask a Superadmin to make changes.'}
            </div>
          )}
        </div>
      </div>

      {/* ── Invite modal ────────────────────────────────────────── */}
      {inviteOpen && (
        <InviteModal
          myRole={me?.role}
          result={inviteResult}
          onSubmit={handleInvite}
          onClose={() => { setInviteOpen(false); setInviteResult(null); }}
        />
      )}
    </div>
  );
}

// ── Stat card (compact KPI without delta) ────────────────────────
function StatCard({ label, value, loading, tone }) {
  return (
    <div className="kpi">
      <div className={`label ${tone === 'warn' ? 'orange' : ''}`}>
        <span className="ic"><Icon name="shield" size={13} /></span>
        {label}
      </div>
      <div className="value">{loading ? '—' : Number(value).toLocaleString()}</div>
    </div>
  );
}

// ── Role picker (inline replacement for the role pill) ───────────
function RolePicker({ current, onPick, onCancel }) {
  const [val, setVal] = useState(current);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <select
        value={val}
        onChange={(e) => setVal(e.target.value)}
        style={{
          padding: '5px 8px',
          border: '1px solid var(--line)',
          borderRadius: 7,
          fontSize: 12,
          background: 'white',
        }}
      >
        {ROLE_OPTIONS.map((r) => (
          <option key={r.value} value={r.value}>{r.label}</option>
        ))}
      </select>
      <button
        className="btn primary sm"
        onClick={() => onPick(val)}
        disabled={val === current}
      >
        Save
      </button>
      <button className="btn ghost sm" onClick={onCancel}>Cancel</button>
    </div>
  );
}

// ── Invite modal ─────────────────────────────────────────────────
function InviteModal({ myRole, result, onSubmit, onClose }) {
  // Restrict the role dropdown to what THIS admin is allowed to grant.
  // A Support Lead sees only SUPPORT_AGENT (one option, locked); a
  // Superadmin sees all three. We default to the FIRST option in that
  // restricted list so a Lead can't accidentally submit a role they
  // can't grant (defence-in-depth on top of the server's canInvite()).
  const allowedRoles = useMemo(() => invitableRoles(myRole), [myRole]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState(allowedRoles[0]?.value || 'SUPPORT_AGENT');
  const [inviteNote, setInviteNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await onSubmit({
        email: email.trim(),
        name: name.trim(),
        role,
        inviteNote: inviteNote.trim(),
      });
    } catch (e) {
      setError(e?.message || 'Invite failed.');
    } finally {
      setBusy(false);
    }
  };

  // Once `result` is set, the modal flips from form view to
  // credentials-handoff view. We don't reset back to the form — if
  // the operator wants to invite another, they close and reopen.
  if (result) {
    return <CredentialsHandoff result={result} onClose={onClose} />;
  }

  const roleMeta = ROLE_OPTIONS.find((r) => r.value === role);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-head">
          <h3>Invite a new admin</h3>
          <button type="button" className="btn ghost sm" onClick={onClose} aria-label="Close">
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="modal-body">
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={field.lbl}>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="person@example.com"
              autoFocus
              required
              style={field.input}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={field.lbl}>Name (optional)</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              style={field.input}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={field.lbl}>Role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={allowedRoles.length <= 1}
              style={{
                ...field.input,
                fontFamily: 'inherit',
                // A locked dropdown (Support Lead with only AGENT
                // available) reads as a static field rather than a
                // disabled UI element — keep the colours normal.
                background: allowedRoles.length <= 1 ? 'var(--bg-2)' : 'white',
                cursor: allowedRoles.length <= 1 ? 'default' : 'pointer',
              }}
            >
              {allowedRoles.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {roleMeta && (
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.4 }}>
                {roleMeta.blurb}
              </div>
            )}
            {allowedRoles.length <= 1 && (
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
                Your role can only invite Support Agents.
              </div>
            )}
          </label>

          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={field.lbl}>Internal note (optional)</span>
            <textarea
              value={inviteNote}
              onChange={(e) => setInviteNote(e.target.value)}
              rows={2}
              placeholder="e.g. Onboarded by Lukman · started 2026-06-15"
              style={{ ...field.input, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </label>

          {error && (
            <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8 }}>{error}</div>
          )}

          <div style={field.help}>
            <strong style={{ color: 'var(--ink-2)' }}>How invites work · </strong>
            We'll create the account and email the new admin a welcome
            message with their temporary password and a link to the console.
            The next screen also shows the password as a backup in case the
            email doesn't arrive — share it out-of-band only if you need to.
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={busy || !email.trim()}>
            {busy ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Credentials handoff (post-invite) ────────────────────────────
//
// Post-invite confirmation view. Adapts its messaging to whether the
// server managed to send the welcome email:
//
//   emailSent=true  — green banner, copy: "Email sent to X with their
//                     credentials. Use the password below as a backup
//                     if they don't receive it." The temp password
//                     remains available because email can land in
//                     spam / get blocked at the recipient's MX / take
//                     several minutes — the inviter shouldn't have
//                     to delete + re-invite just because of an Outlook
//                     filter.
//
//   emailSent=false — amber banner, copy: "Email delivery failed —
//                     share these credentials out-of-band." Same
//                     password field, same copy button, but the
//                     framing tells the inviter they're now the
//                     primary channel.
//
// Either way the password is shown once. Closing the modal discards
// it (only the bcrypt hash is stored DB-side); the only recovery is
// delete + re-invite.
function CredentialsHandoff({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const a  = result?.admin || {};
  const pw = result?.tempPassword || '';
  const emailSent  = result?.emailSent === true;
  const emailError = result?.emailError || null;

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText?.(pw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Older browser / iframe sandbox — fall back to selection.
      const el = document.getElementById('temp-pw-field');
      if (el) { el.select?.(); document.execCommand?.('copy'); setCopied(true); }
    }
  };

  // Banner palette + copy varies on emailSent. Tokens chosen to
  // match existing pill colours so the modal feels consistent with
  // the rest of the admin UI.
  const banner = emailSent
    ? {
        bg: 'var(--green-soft, #DDF6E5)',
        border: 'var(--green, #10A37F)',
        title: 'Invite sent',
        body: (
          <>
            We emailed <strong>{a.email}</strong> ({ROLE_LABELS[a.role] || a.role}) their{' '}
            temporary password. The password is also shown below — use it as a backup if{' '}
            they don't receive the email (check spam first).
          </>
        ),
      }
    : {
        bg: 'var(--amber-soft, #FFF1D6)',
        border: '#D88E1B',
        title: 'Email delivery failed',
        body: (
          <>
            Account for <strong>{a.email}</strong> ({ROLE_LABELS[a.role] || a.role}) is{' '}
            ready, but the welcome email couldn't be sent. Share the password below{' '}
            out-of-band — it's shown <strong>once</strong>.
            {/* Server-provided reason — mapped from the raw Resend
                error in AdminInviteEmailService.friendlyReason().
                Renders in a darker tone underneath the main body
                copy so it reads as a diagnostic note rather than
                competing with the primary message. */}
            {emailError && (
              <div style={{
                marginTop: 10,
                padding: '8px 10px',
                background: 'rgba(255,255,255,0.6)',
                border: '1px solid rgba(216,142,27,0.4)',
                borderRadius: 6,
                fontSize: 12,
                color: '#7C5510',
                lineHeight: 1.5,
              }}>
                <strong style={{ display: 'block', marginBottom: 2, fontFamily: 'Geist Mono, monospace', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Reason
                </strong>
                {emailError}
              </div>
            )}
          </>
        ),
      };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Invite created</h3>
          <button type="button" className="btn ghost sm" onClick={onClose} aria-label="Close">
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="modal-body">
          <div style={{
            padding: 14,
            background: banner.bg,
            borderLeft: `3px solid ${banner.border}`,
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 13,
            color: 'var(--ink)',
            lineHeight: 1.5,
          }}>
            <strong style={{ display: 'block', marginBottom: 4 }}>{banner.title}</strong>
            {banner.body}
          </div>

          <div style={{ marginBottom: 10 }}>
            <span style={field.lbl}>Email</span>
            <div className="mono" style={{ fontSize: 14, padding: '8px 10px', background: 'var(--bg-2)', borderRadius: 7 }}>
              {a.email}
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <span style={field.lbl}>Temporary password</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                id="temp-pw-field"
                type="text"
                readOnly
                value={pw}
                onFocus={(e) => e.target.select()}
                style={{
                  ...field.input,
                  fontFamily: 'Geist Mono, monospace',
                  fontSize: 14,
                  letterSpacing: '0.04em',
                  background: '#FFFAE6',
                  borderColor: '#F2CD5C',
                  marginBottom: 0,
                }}
              />
              <button type="button" className="btn primary" onClick={copy}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          <div style={field.help}>
            <strong style={{ color: 'var(--ink-2)' }}>Important · </strong>
            This password is only displayed once. If you lose it, delete the
            account and re-invite — there's no reset endpoint for temp
            credentials. Remind the new admin to change it from Settings
            after their first sign-in.
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Inline style fragments shared across the modal ──────────────
const field = {
  lbl: {
    display: 'block',
    fontSize: 12,
    color: 'var(--ink-2)',
    marginBottom: 4,
    fontFamily: 'Geist Mono, monospace',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  input: {
    width: '100%',
    padding: '9px 11px',
    border: '1px solid var(--line)',
    borderRadius: 8,
    fontSize: 14,
    background: 'white',
    color: 'var(--ink)',
    outline: 'none',
  },
  help: {
    padding: 10,
    background: 'var(--bg-2)',
    borderRadius: 8,
    fontSize: 11.5,
    color: 'var(--ink-3)',
    lineHeight: 1.5,
  },
};

// ── Local helpers ────────────────────────────────────────────────
function initials(name) {
  if (!name) return '—';
  return name.split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

function agoString(iso) {
  const ts = new Date(iso).getTime();
  if (!ts) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1)  return 'now';
  if (mins < 60) return mins + 'm ago';
  const h = Math.floor(mins / 60);
  if (h < 24)    return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 30)    return d + 'd ago';
  return Math.floor(d / 30) + 'mo ago';
}
